import {joiResolver} from '@hookform/resolvers/joi';
import {AxiosError} from 'axios';
import {useState} from 'react';
import {useForm, UseFormReturn} from 'react-hook-form';
import {useHistory} from 'react-router-dom';
import {GenericErrorResponse} from '../../../api';
import {SetPasswordData} from '../../../models';
import schemaSetPassword from '../schema/schemaSetPassword';
import RouteKey from '../../../routes/key';
import {
  SetPasswordRequest,
  usePostSetPassword
} from '../../../api-refactored/user/usePostSetPassword';
import {UseCustomMutationReturn} from '../../../api-refactored/utils/useCustomMutation';

export interface useSetPasswordFormReturn {
  /**
   * Function to trigger api to create candidate
   */
  setPassword: (formData: SetPasswordData) => void;
  /**
   * React hook form object for the create candidate form
   */
  form: UseFormReturn<SetPasswordData>;
  /**
   * Extra handling error message for the create candidate api
   */
  submitError: string;
  /**
   * React query mutation object
   */
  setPasswordMutation: UseCustomMutationReturn<
      void,
      AxiosError<GenericErrorResponse>,
      SetPasswordRequest,
      unknown
  >;
}

/**
 * A hook to handle candidate creation logic
 *
 * @param link Target user link
 */
export const useSetPasswordForm = (link: string): useSetPasswordFormReturn => {
  const [submitError, setSubmitError] = useState('');
  const history = useHistory();

  const {auth: authRoute} = RouteKey.child;
  const {activated} = RouteKey.auth.child;

  const form = useForm<SetPasswordData>({
    mode: 'onSubmit',
    resolver: joiResolver(schemaSetPassword),
  });

  const setPasswordMutation = usePostSetPassword({
    onSuccess: () => {
      history.push(`/${authRoute}/${activated}`);
    },
    onError: (err) => accountActivationFailCallback(err)
  });

  const accountActivationFailCallback = (err: AxiosError<GenericErrorResponse>) => {
    const applicationCode: string = err.response?.data.code!;
    switch (err.response?.status) {
      case undefined:
        setSubmitError('');
        break;
      case 400:
        if (applicationCode === '1200') {
          setSubmitError(
              'Invalid Password! Please do not use your last 3 password, your username, or an easily guessable password as your password.'
          );
        } else {
          setSubmitError('Bad Request');
        }
        break;
      default:
        setSubmitError('Unknown error. Please try again.');
        break;
    }
  };

  const setPassword = (formData: SetPasswordData) => {
    const data: SetPasswordRequest = {
      link,
      password: formData.setPassword,
      otpType: formData.otpType,
      email: formData.email,
      countryCode: parseInt(formData.countryCode, 10),
      number: formData.number,
    };
    setPasswordMutation.mutate(data);
  };

  return {
    form,
    setPassword,
    submitError,
    setPasswordMutation,
  };
};
